import { staleTimes, useQueries } from '@platform/api-client';
import type { TypedClient } from '@platform/openapi';
import { indexOptions } from '../catalog/options.ts';
import { useOptions, useTenantDetail } from '../catalog/useCatalogQueries.ts';
import { queryKeys, usePageContext } from '../usePageContext.ts';
import { labelSubjectsOf, type LabelSubject } from './labels.ts';

export type LabelSubjects =
  | { readonly state: 'loading' }
  | { readonly state: 'failed'; readonly error: unknown; readonly retry: () => void }
  | { readonly state: 'ready'; readonly subjects: readonly LabelSubject[] };

export const useLabelSubjects = (api: TypedClient, productIds: readonly string[]): LabelSubjects => {
  const { tenantId } = usePageContext();
  const products = useQueries({
    queries: productIds.map((id) => ({
      queryKey: queryKeys.product(tenantId, id),
      queryFn: () => api.get('/admin/products/{id}', { path: { id } }),
      staleTime: staleTimes.catalog,
    })),
  });
  const options = useOptions(api);
  const tenant = useTenantDetail(api);
  const queries = [...products, options, tenant];
  const failed = queries.find((query) => query.isError);
  if (failed !== undefined)
    return {
      state: 'failed',
      error: failed.error,
      retry: () => {
        queries.filter((query) => query.isError).forEach((query) => void query.refetch());
      },
    };
  if (
    options.data === undefined ||
    tenant.data === undefined ||
    products.some((query) => query.data === undefined)
  )
    return { state: 'loading' };
  const optionList = options.data.items;
  return {
    state: 'ready',
    subjects: labelSubjectsOf(
      products.flatMap((query) => (query.data === undefined ? [] : [query.data])),
      (locale) => indexOptions(optionList, locale),
      tenant.data.currency,
    ),
  };
};
